export const SCHOOL_TIME_ZONE = 'Europe/Kyiv';

/** Local wall-clock window, e.g. { from: '21:00', to: '08:00' } (may wrap past midnight). */
export interface QuietHours {
  from: string;
  to: string;
}

interface WallTime {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function wallTime(date: Date, timeZone: string): WallTime {
  let fmt = formatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, fmt);
  }
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

function offsetMinutes(date: Date, timeZone: string): number {
  const w = wallTime(date, timeZone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return Math.round((asUtc - date.getTime()) / 60_000);
}

/** Converts a local wall-clock time to a UTC instant, correct across DST switches. */
function zonedToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const first = offsetMinutes(new Date(guess), timeZone);
  let instant = guess - first * 60_000;
  const second = offsetMinutes(new Date(instant), timeZone);
  if (second !== first) instant = guess - second * 60_000;
  return new Date(instant);
}

function parseHm(value: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) throw new Error(`invalid time "${value}", expected HH:MM`);
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) throw new Error(`invalid time "${value}"`);
  return h * 60 + m;
}

export function isWithinQuietHours(
  now: Date,
  quiet: QuietHours,
  timeZone = SCHOOL_TIME_ZONE,
): boolean {
  const from = parseHm(quiet.from);
  const to = parseHm(quiet.to);
  if (from === to) return false;
  const w = wallTime(now, timeZone);
  const minutes = w.hour * 60 + w.minute;
  return from < to ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

/**
 * When a non-urgent notification may be delivered: `now` outside quiet hours, otherwise the
 * end of the quiet window. Safety notifications (alerts, urgent announcements) bypass this.
 */
export function deliverAfter(now: Date, quiet: QuietHours, timeZone = SCHOOL_TIME_ZONE): Date {
  if (!isWithinQuietHours(now, quiet, timeZone)) return now;
  const from = parseHm(quiet.from);
  const to = parseHm(quiet.to);
  const w = wallTime(now, timeZone);
  const minutes = w.hour * 60 + w.minute;
  // in a wrapping window (21:00–08:00) evening times end tomorrow morning
  const endsTomorrow = from > to && minutes >= from;
  const base = new Date(Date.UTC(w.year, w.month - 1, w.day + (endsTomorrow ? 1 : 0)));
  return zonedToUtc(
    base.getUTCFullYear(),
    base.getUTCMonth() + 1,
    base.getUTCDate(),
    Math.floor(to / 60),
    to % 60,
    timeZone,
  );
}
